/**
 * The peer colours for presence surfaces: the eight chart-series ramp values
 * (cornflower, gold, pink, shamrock, heliotrope, poppy, reef, marigold) fixed
 * to sRGB hex from their `tokens.css` oklch values, because the presence wire
 * pins `colour: '#rrggbb'` and a hex cannot be theme-dependent. Assignment is
 * FNV-1a over the userId, so a collaborator keeps one colour on every surface
 * and every platform. The Dart twin (`packages/components/dart`
 * `presence_palette.dart`) mirrors this table verbatim; its parity test reads
 * this file.
 */

export type PeerPaletteEntry = {
  hex: string;
  on: string;
};

/**
 * Every entry is a FLAT ramp token, and that is a requirement rather than a
 * coincidence. Slots 4 and 5 were originally the chart-series aliases
 * `green-shamrock` and `purple-heliotrope`, which are `light-dark()` PAIRS: the
 * flat copy took their light rung, which measures 2.69:1 and 2.71:1 on
 * `background-paper-raised` in dark against a 3:1 bar, and their dark rungs
 * exist precisely to clear it. A flat hex cannot carry a pair, so those two are
 * replaced by `green-fern` and `purple-mauve`, which clear 3:1 on every surface
 * in both schemes (3.84 and 3.54 at worst). Reach for shamrock here again and
 * the failure comes back; `palette-drift.test.ts` refuses any pair-valued
 * source.
 */
export const PEER_PALETTE: readonly PeerPaletteEntry[] = [
  { hex: '#567aca', on: '#000000' },
  { hex: '#ac6508', on: '#000000' },
  { hex: '#e16288', on: '#000000' },
  { hex: '#728856', on: '#000000' },
  { hex: '#a16792', on: '#000000' },
  { hex: '#fa4c3b', on: '#000000' },
  { hex: '#189a90', on: '#000000' },
  { hex: '#d46d00', on: '#000000' },
];

export const peerPaletteIndex = (userId: string): number => {
  let hash = 0x811c9dc5;
  for (const byte of new TextEncoder().encode(userId)) {
    hash ^= byte;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash % PEER_PALETTE.length;
};

export const peerColour = (userId: string): PeerPaletteEntry => PEER_PALETTE[peerPaletteIndex(userId)] as PeerPaletteEntry;

/**
 * The text colour for a published hex: the table pairing when the hex is ours,
 * otherwise whichever of black and white contrasts better, so an off-palette
 * publisher still gets a readable flag.
 */
export const peerOnColour = (colour: string): string => {
  const entry = PEER_PALETTE.find(candidate => candidate.hex === colour.toLowerCase());
  if (entry) return entry.on;
  const match = /^#([0-9a-f]{6})$/i.exec(colour);
  if (!match) return '#000000';
  const [red, green, blue] = [0, 2, 4].map(start => {
    const channel = Number.parseInt((match[1] as string).slice(start, start + 2), 16) / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * (red as number) + 0.7152 * (green as number) + 0.0722 * (blue as number);
  return luminance >= 0.1791 ? '#000000' : '#ffffff';
};
